import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES, categoryById, isSensitive } from "@/lib/categories";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Eye, EyeOff, RefreshCw, Lock } from "lucide-react";
import { toast } from "sonner";

const genPassword = (len = 18) => {
  const chars =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()-_=+";
  let out = "";
  const arr = new Uint32Array(len);
  crypto.getRandomValues(arr);
  for (let i = 0; i < len; i++) out += chars[arr[i] % chars.length];
  return out;
};

export default function SecretFormModal({ open, onOpenChange, initial, defaultCategory }) {
  const { user } = useAuth();
  const [category, setCategory] = useState(defaultCategory || "server");
  const [title, setTitle] = useState("");
  const [fields, setFields] = useState({});
  const [tags, setTags] = useState("");
  const [adminOnly, setAdminOnly] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [saving, setSaving] = useState(false);

  const reset = useCallback(() => {
    if (initial) {
      setCategory(initial.category);
      setTitle(initial.title || "");
      setFields(initial.fields || {});
      setTags((initial.tags || []).join(", "));
      setAdminOnly(!!initial.admin_only);
    } else {
      setCategory(defaultCategory || "server");
      setTitle("");
      setFields({});
      setTags("");
      setAdminOnly(false);
    }
    setRevealed({});
  }, [initial, defaultCategory]);

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const cat = categoryById(category);

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Başlık gereklidir");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        category,
        fields,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        admin_only: adminOnly,
      };
      if (initial) {
        await api.put(`/secrets/${initial.secret_id}`, payload);
        toast.success("Kayıt güncellendi");
      } else {
        await api.post("/secrets", payload);
        toast.success("Kayıt oluşturuldu");
      }
      window.dispatchEvent(new CustomEvent("vaultops:refresh"));
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="secret-form-modal"
        className="bg-[#0d121f] border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Lock className="w-4 h-4 text-emerald-400" />
            {initial ? "Kaydı Düzenle" : "Yeni Gizli Kayıt"}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Kategoriye uygun alanları doldurun. Parolalar sadece sizin ve yetkili
            ekip üyelerinizin erişimine kapalı tutulur.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_200px] gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-400">
                Başlık
              </Label>
              <Input
                data-testid="secret-form-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Örn. srv-prod-01.corp"
                className="bg-[#111726] border-slate-800 text-slate-100 mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-400">
                Kategori
              </Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger
                  data-testid="secret-form-category"
                  className="bg-[#111726] border-slate-800 text-slate-100 mt-1"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111726] border-slate-800 text-slate-100">
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`category-option-${c.id}`}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {cat.fields.map((f) => {
              const sensitive = f.type === "password" || isSensitive(f.key);
              const show = revealed[f.key];
              return (
                <div
                  key={f.key}
                  className={f.type === "textarea" ? "sm:col-span-2" : ""}
                >
                  <Label className="text-xs uppercase tracking-widest text-slate-400">
                    {f.label}
                  </Label>
                  {f.type === "textarea" ? (
                    <Textarea
                      data-testid={`field-${f.key}`}
                      value={fields[f.key] || ""}
                      onChange={(e) =>
                        setFields({ ...fields, [f.key]: e.target.value })
                      }
                      rows={3}
                      className="bg-[#111726] border-slate-800 text-slate-100 mt-1 font-mono text-sm"
                    />
                  ) : (
                    <div className="relative mt-1">
                      <Input
                        data-testid={`field-${f.key}`}
                        type={sensitive && !show ? "password" : f.type === "date" ? "date" : "text"}
                        value={fields[f.key] || ""}
                        onChange={(e) =>
                          setFields({ ...fields, [f.key]: e.target.value })
                        }
                        className="bg-[#111726] border-slate-800 text-slate-100 pr-20 font-mono text-sm"
                      />
                      {sensitive && (
                        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() =>
                              setFields({ ...fields, [f.key]: genPassword() })
                            }
                            className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-emerald-400"
                            data-testid={`gen-${f.key}`}
                            title="Parola üret"
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setRevealed({ ...revealed, [f.key]: !show })
                            }
                            className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-emerald-400"
                            data-testid={`toggle-${f.key}`}
                          >
                            {show ? (
                              <EyeOff className="w-3.5 h-3.5" />
                            ) : (
                              <Eye className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-400">
              Etiketler (virgülle ayırın)
            </Label>
            <Input
              data-testid="secret-form-tags"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="production, ankara-dc, critical"
              className="bg-[#111726] border-slate-800 text-slate-100 mt-1"
            />
          </div>

          {user?.role === "admin" && (
            <div className="flex items-center justify-between p-3 rounded-lg bg-[#111726] border border-slate-800">
              <div>
                <div className="text-sm text-slate-100">Sadece Admin Görebilsin</div>
                <div className="text-xs text-slate-400">
                  Bu kayıt yalnızca admin rolündeki kullanıcılara görünür olur.
                </div>
              </div>
              <Switch
                data-testid="secret-form-admin-only"
                checked={adminOnly}
                onCheckedChange={setAdminOnly}
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-slate-300 hover:bg-slate-800"
            data-testid="secret-form-cancel"
          >
            Vazgeç
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
            data-testid="save-secret-form-button"
          >
            {saving ? "Kaydediliyor..." : initial ? "Güncelle" : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
